import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Inbox, Send, FileText, Trash2, Star, Search, RefreshCw,
  Plus, ChevronDown, ChevronRight, Settings, LogOut, AlertCircle,
  Loader, Menu, X, Check, MoreVertical, ArrowLeft,
  Maximize2, Minimize2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ConnectAccountModal from './ConnectAccountModal';
import ComposeModal from './ComposeModal';
import MessageThread from './MessageThread';
import MainLayout from '../../components/layout/MainLayout';
import './EmailInbox.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 'Starred' is not a real IMAP mailbox — it's a flag, not a folder.
// Using it as a folder name would cause syncFolder to fail on the server.
const FOLDERS = [
  { key: 'INBOX',   label: 'Inbox',   icon: Inbox },
  { key: 'Sent',    label: 'Sent',    icon: Send },
  { key: 'Drafts',  label: 'Drafts',  icon: FileText },
  { key: 'Trash',   label: 'Trash',   icon: Trash2 }
];

export default function EmailInboxPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = localStorage.getItem('crm_token');

  const [accounts, setAccounts]         = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);
  const [activeFolder, setActiveFolder]  = useState('INBOX');
  const [messages, setMessages]          = useState([]);
  const [totalMessages, setTotal]        = useState(0);
  const [unreadCount, setUnreadCount]    = useState(0);
  const [page, setPage]                  = useState(1);
  const [selectedMsg, setSelectedMsg]    = useState(null);
  const [search, setSearch]              = useState('');
  const [searchInput, setSearchInput]    = useState('');
  const [loading, setLoading]            = useState(false);
  const [syncing, setSyncing]            = useState(false);
  const [showConnect, setShowConnect]    = useState(false);
  const [showCompose, setShowCompose]    = useState(false);
  const [composeData, setComposeData]    = useState(null);
  const [sidebarOpen, setSidebarOpen]    = useState(true);
  const [isFullscreen, setIsFullscreen]  = useState(false);
  const [error, setError]                = useState('');
  const [customFolders, setCustomFolders]= useState([]);
  const [foldersExpanded, setFoldersExpanded] = useState(false);
  const searchTimeout = useRef(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ── Load accounts ──────────────────────────────────────────────────────────
  const loadAccounts = useCallback(async () => {
    try {
      const r = await fetch(`${API}/email-inbox/accounts`, { headers });
      if (!r.ok) throw new Error('Failed to load accounts');
      const data = await r.json();
      setAccounts(data);
      if (data.length > 0 && !activeAccount) {
        setActiveAccount(data[0]);
      }
    } catch (e) {
      setError(e.message);
    }
  }, [token]); // eslint-disable-line

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // ── Load messages ──────────────────────────────────────────────────────────
  // `nextPage` is passed explicitly to avoid React async state race condition.
  // If nextPage is undefined, use the current `page` value (for initial load).
  const loadMessages = useCallback(async (reset = false, nextPage) => {
    if (!activeAccount) return;
    setLoading(true);
    setError('');
    try {
      const p = reset ? 1 : (nextPage !== undefined ? nextPage : page);
      const params = new URLSearchParams({
        folder: activeFolder, page: p, limit: 30, search
      });
      const r = await fetch(`${API}/email-inbox/accounts/${activeAccount._id}/messages?${params}`, { headers });
      if (!r.ok) throw new Error('Failed to load messages');
      const data = await r.json();

      if (reset || p === 1) {
        setMessages(data.messages);
        setPage(1);
      } else {
        // Bug fix: if DB returned 0 results but the total says there should be more,
        // it means the local cache ran out. Sync more from IMAP then reload this page.
        if (data.messages.length === 0 && data.total > 0) {
          setSyncing(true);
          await fetch(`${API}/email-inbox/accounts/${activeAccount._id}/sync`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ folder: activeFolder, limit: p * 30 + 50 })
          });
          setSyncing(false);
          // Reload the same page after syncing more from server
          const r2 = await fetch(`${API}/email-inbox/accounts/${activeAccount._id}/messages?${params}`, { headers });
          if (r2.ok) {
            const data2 = await r2.json();
            setMessages(prev => [...prev, ...data2.messages]);
            setTotal(data2.total);
          }
        } else {
          setMessages(prev => [...prev, ...data.messages]);
        }
      }
      setTotal(data.total);
      setUnreadCount(data.unreadCount || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeAccount, activeFolder, page, search, token]); // eslint-disable-line

  useEffect(() => {
    if (!activeAccount) return;
    // When switching folders, load cached messages first.
    // If the folder has no cached messages (e.g. first time opening Sent/Drafts/Trash),
    // auto-trigger a live IMAP sync so the user doesn't see an empty screen.
    const autoSync = async () => {
      const params = new URLSearchParams({ folder: activeFolder, page: 1, limit: 1 });
      try {
        const r = await fetch(`${API}/email-inbox/accounts/${activeAccount._id}/messages?${params}`, { headers });
        if (r.ok) {
          const data = await r.json();
          if (data.total === 0 && activeFolder !== 'Starred') {
            // No cached messages — trigger a background sync then reload
            setSyncing(true);
            await fetch(`${API}/email-inbox/accounts/${activeAccount._id}/sync`, {
              method: 'POST',
              headers,
              body: JSON.stringify({ folder: activeFolder, limit: 50 })
            });
            setSyncing(false);
          }
        }
      } catch (_) { setSyncing(false); }
      loadMessages(true);
    };
    autoSync();
  }, [activeAccount, activeFolder, search]); // eslint-disable-line

  // ── Sync ───────────────────────────────────────────────────────────────────
  const handleSync = async () => {
    if (!activeAccount || syncing) return;
    setSyncing(true);
    try {
      await fetch(`${API}/email-inbox/accounts/${activeAccount._id}/sync`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ folder: activeFolder })
      });
      await loadMessages(true);
    } catch (e) {
      setError('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // ── Load custom folders ────────────────────────────────────────────────────
  const loadFolders = async () => {
    if (!activeAccount) return;
    try {
      const r = await fetch(`${API}/email-inbox/accounts/${activeAccount._id}/folders`, { headers });
      const data = await r.json();
      const known = FOLDERS.map(f => f.key);
      setCustomFolders((data.folders || []).filter(f => !known.includes(f)));
    } catch (_) {}
  };

  useEffect(() => {
    if (activeAccount) loadFolders();
  }, [activeAccount]); // eslint-disable-line

  // ── Select message ─────────────────────────────────────────────────────────
  const handleSelectMessage = async (msg) => {
    setSelectedMsg(msg);
    if (!msg.isRead) {
      setMessages(prev => prev.map(m => m.uid === msg.uid ? { ...m, isRead: true } : m));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  // ── Star toggle ────────────────────────────────────────────────────────────
  const handleStar = async (e, msg) => {
    e.stopPropagation();
    if (!activeAccount) return;
    try {
      await fetch(`${API}/email-inbox/accounts/${activeAccount._id}/messages/${msg.uid}/star`, {
        method: 'PATCH', headers, body: JSON.stringify({ folder: msg.folder })
      });
      setMessages(prev => prev.map(m => m.uid === msg.uid ? { ...m, isStarred: !m.isStarred } : m));
      if (selectedMsg?.uid === msg.uid) setSelectedMsg(prev => ({ ...prev, isStarred: !prev.isStarred }));
    } catch (_) {}
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (msg) => {
    if (!activeAccount) return;
    try {
      await fetch(`${API}/email-inbox/accounts/${activeAccount._id}/messages/${msg.uid}?folder=${msg.folder}`, {
        method: 'DELETE', headers
      });
      setMessages(prev => prev.filter(m => m.uid !== msg.uid));
      if (selectedMsg?.uid === msg.uid) setSelectedMsg(null);
    } catch (_) {}
  };

  // ── Reply ──────────────────────────────────────────────────────────────────
  const handleReply = (msg) => {
    setComposeData({
      to: [msg.fromEmail],
      subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`,
      inReplyTo: msg.messageId,
      references: msg.messageId,
      quoteHtml: msg.bodyHtml || `<br/><blockquote>${msg.bodyText || ''}</blockquote>`
    });
    setShowCompose(true);
  };

  // ── Search ─────────────────────────────────────────────────────────────────
  const handleSearchInput = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearch(val), 500);
  };

  // ── Account connected callback ─────────────────────────────────────────────
  const handleAccountConnected = (acc) => {
    setAccounts(prev => [acc, ...prev]);
    setActiveAccount(acc);
    setShowConnect(false);
  };

  // ── Remove account ─────────────────────────────────────────────────────────
  const handleRemoveAccount = async (accountId) => {
    if (!window.confirm("Are you sure you want to remove this email account? This action cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/email-inbox/accounts/${accountId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        setAccounts(prev => prev.filter(a => a._id !== accountId));
        if (activeAccount?._id === accountId) {
          const remaining = accounts.filter(a => a._id !== accountId);
          setActiveAccount(remaining.length > 0 ? remaining[0] : null);
          setMessages([]);
          setTotal(0);
        }
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to remove account');
      }
    } catch (err) {
      setError("Failed to remove account");
    }
  };

  // ── Format date ────────────────────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 86400000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diff < 7 * 86400000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // ── No account connected ───────────────────────────────────────────────────
  if (!loading && accounts.length === 0) {
    return (
      <MainLayout title="Email Inbox">
        <div className="ei-empty-state">
        <div className="ei-empty-card">
          <div className="ei-empty-icon">
            <Mail size={48} />
          </div>
          <h2>Connect your Email</h2>
          <p>Link your IMAP mailbox to read, reply and manage emails directly inside the CRM.</p>
          <button className="ei-btn-primary ei-btn-lg" onClick={() => setShowConnect(true)}>
            <Plus size={18} /> Connect Email Account
          </button>
        </div>
        {showConnect && (
          <ConnectAccountModal
            onClose={() => setShowConnect(false)}
            onConnected={handleAccountConnected}
          />
        )}
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Email Inbox">
      <div className={`ei-shell ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* ── SIDEBAR ─────────────────────────────────────────────────────── */}
      <div className={`ei-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="ei-sidebar-header">
          <div className="ei-brand">
            <Mail size={20} className="ei-brand-icon" />
            {sidebarOpen && <span>Email Inbox</span>}
          </div>
          <button className="ei-icon-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <button className="ei-compose-btn" onClick={() => { setComposeData(null); setShowCompose(true); }}>
          <Plus size={18} />
          {sidebarOpen && <span>Compose</span>}
        </button>

        {/* Folder list */}
        <nav className="ei-nav">
          {FOLDERS.map(({ key, label, icon: Icon }) => {
            const isActive = activeFolder === key;
            return (
              <button
                key={key}
                className={`ei-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => { setActiveFolder(key); setSelectedMsg(null); }}
              >
                <Icon size={18} />
                {sidebarOpen && (
                  <>
                    <span>{label}</span>
                    {key === 'INBOX' && unreadCount > 0 && (
                      <span className="ei-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                    )}
                  </>
                )}
              </button>
            );
          })}

          {customFolders.length > 0 && (
            <>
              <button className="ei-nav-section-toggle" onClick={() => setFoldersExpanded(!foldersExpanded)}>
                {foldersExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {sidebarOpen && <span>More Folders</span>}
              </button>
              {foldersExpanded && customFolders.map(f => (
                <button
                  key={f}
                  className={`ei-nav-item ${activeFolder === f ? 'active' : ''}`}
                  onClick={() => { setActiveFolder(f); setSelectedMsg(null); }}
                >
                  <Mail size={18} />
                  {sidebarOpen && <span className="ei-folder-name">{f}</span>}
                </button>
              ))}
            </>
          )}
        </nav>

        {/* Account switcher */}
        {sidebarOpen && accounts.length > 0 && (
          <div className="ei-accounts">
            <div className="ei-accounts-label">Accounts</div>
            {accounts.map(acc => (
              <div
                key={acc._id}
                className={`ei-account-item ${activeAccount?._id === acc._id ? 'active' : ''}`}
                style={{ padding: '4px 8px' }}
              >
                <div 
                  onClick={() => { setActiveAccount(acc); setSelectedMsg(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, overflow: 'hidden' }}
                >
                  <div className="ei-account-avatar">{acc.emailAddress?.[0]?.toUpperCase()}</div>
                  <div className="ei-account-info">
                    <div className="ei-account-name">{acc.label}</div>
                    <div className="ei-account-email">{acc.emailAddress}</div>
                  </div>
                </div>
                <button
                  className="ei-icon-btn"
                  style={{ width: '28px', height: '28px', background: 'transparent', border: 'none', padding: 0 }}
                  onClick={(e) => { e.stopPropagation(); handleRemoveAccount(acc._id); }}
                  title="Remove Account"
                >
                  <LogOut size={14} />
                </button>
              </div>
            ))}
            <button className="ei-add-account" onClick={() => setShowConnect(true)}>
              <Plus size={14} /> Add account
            </button>
          </div>
        )}
      </div>

      {/* ── MAIN AREA ───────────────────────────────────────────────────── */}
      <div className="ei-main">
        <div className="ei-topbar">
          <h2 className="ei-folder-title">
            {FOLDERS.find(f => f.key === activeFolder)?.label || activeFolder}
            {activeAccount && <span className="ei-account-pill">{activeAccount.emailAddress}</span>}
          </h2>
          <div className="ei-topbar-right">
            <div className="ei-search-wrap">
              <Search size={16} className="ei-search-icon" />
              <input
                className="ei-search-input"
                placeholder="Search emails..."
                value={searchInput}
                onChange={e => handleSearchInput(e.target.value)}
              />
              {searchInput && (
                <button className="ei-search-clear" onClick={() => { setSearchInput(''); setSearch(''); }}>
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              className={`ei-icon-btn ei-sync-btn ${syncing ? 'spinning' : ''}`}
              onClick={handleSync}
              title="Sync inbox"
            >
              <RefreshCw size={18} />
            </button>
            <button
              className="ei-icon-btn"
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? "Exit full screen" : "Full screen"}
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
          </div>
        </div>

        {error && (
          <div className="ei-error-bar">
            <AlertCircle size={16} /> {error}
            <button onClick={() => setError('')}><X size={14} /></button>
          </div>
        )}

        <div className="ei-content">
          {/* Message list */}
          <div className={`ei-msg-list ${selectedMsg ? 'hidden-mobile' : ''}`}>
            {loading && messages.length === 0 ? (
              <div className="ei-list-loading">
                <Loader size={28} className="ei-spinner" />
                <span>Loading messages…</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="ei-list-empty">
                <Inbox size={36} />
                <p>{search ? 'No messages match your search.' : 'No messages in this folder.'}</p>
              </div>
            ) : (
              <>
                {messages.map(msg => (
                  <div
                    key={`${msg.folder}-${msg.uid}`}
                    className={`ei-msg-row ${!msg.isRead ? 'unread' : ''} ${selectedMsg?.uid === msg.uid ? 'selected' : ''}`}
                    onClick={() => handleSelectMessage(msg)}
                  >
                    <button
                      className={`ei-star-btn ${msg.isStarred ? 'starred' : ''}`}
                      onClick={(e) => handleStar(e, msg)}
                    >
                      <Star size={16} fill={msg.isStarred ? 'currentColor' : 'none'} />
                    </button>
                    <div className="ei-msg-avatar">
                      {(msg.fromName || msg.fromEmail || '?')[0].toUpperCase()}
                    </div>
                    <div className="ei-msg-info">
                      <div className="ei-msg-from">
                        <span className="ei-msg-sender">{msg.fromName || msg.fromEmail}</span>
                        <span className="ei-msg-date">{formatDate(msg.date)}</span>
                      </div>
                      <div className="ei-msg-subject">{msg.subject}</div>
                      <div className="ei-msg-snippet">{msg.snippet}</div>
                    </div>
                    {msg.hasAttachments && (
                      <div className="ei-attachment-dot" title="Has attachments">📎</div>
                    )}
                  </div>
                ))}
                {messages.length < totalMessages && (
                  <button className="ei-load-more" onClick={() => {
                    // Compute the next page here and pass it directly.
                    // setPage is async so we cannot rely on `page` being
                    // updated by the time loadMessages reads it.
                    const nextPage = page + 1;
                    setPage(nextPage);
                    loadMessages(false, nextPage);
                  }}>
                    Load more ({totalMessages - messages.length} remaining)
                  </button>
                )}
              </>
            )}
          </div>

          {/* Message viewer */}
          {selectedMsg ? (
            <MessageThread
              message={selectedMsg}
              account={activeAccount}
              onReply={handleReply}
              onDelete={handleDelete}
              onStar={(msg) => handleStar({ stopPropagation: () => {} }, msg)}
              onBack={() => setSelectedMsg(null)}
            />
          ) : (
            <div className="ei-viewer-placeholder">
              <Mail size={48} />
              <p>Select a message to read</p>
            </div>
          )}
        </div>
      </div>

      {/* ── MODALS ─────────────────────────────────────────────────────── */}
      {showConnect && (
        <ConnectAccountModal
          onClose={() => setShowConnect(false)}
          onConnected={handleAccountConnected}
        />
      )}
      {showCompose && (
        <ComposeModal
          account={activeAccount}
          initialData={composeData}
          onClose={() => { setShowCompose(false); setComposeData(null); }}
          onSent={() => {
            setShowCompose(false);
            setComposeData(null);
            if (activeFolder === 'Sent') loadMessages(true);
          }}
        />
      )}
      </div>
    </MainLayout>
  );
}
